import { ApiError, type SalesBreakdown, type SalesRow } from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import { formatMoney } from "@khazzin/i18n/money";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { BarSeries, ChartCard, type ChartData, LineTrend } from "../charts";
import type { DateRange } from "../patterns/dateRange";
import { EmptyState, ErrorState, ForbiddenState, KpiTile } from "../patterns/page";
import { DataTable } from "../patterns/table";
import { Button } from "../primitives";
import { Skeleton } from "../primitives/Skeleton";
import { formatCount, formatDay, formatRatio } from "./format";
import {
	barRows,
	currenciesIn,
	deltaPct,
	SALES_CHANNELS,
	type SalesDim,
	type SalesTile,
	sparkOf,
	storeDelta,
	tilesOf,
	trendRows,
	UNASSIGNED,
} from "./sales";

/**
 * Sales & Revenue (EP13-S10, DA-13-06) below its filter bar: four tiles with
 * their change on the previous period and a sparkline, revenue per day with
 * the previous period dashed, revenue by channel and by province (a province
 * drills to its cities), and the top SKUs and stores. Money is per currency,
 * never summed across two.
 *
 * Shared since EP10-S13: the Admin Console wraps it in its filter bar and its
 * reads, and the Store Portal in a range picker and `/portal/dashboards`.
 * `variant="store"` has no store column and no store ranking.
 */

type Query = UseQueryResult<SalesBreakdown>;

export interface SalesPanelsProps {
	t: Translate;
	locale: Locale;
	variant: "admin" | "store";
	range: DateRange;
	compare: boolean;
	/** The province drilled into, if any. */
	province: string | undefined;
	blocks: {
		summary: Query;
		trend: Query;
		channels: Query;
		geography: Query;
		topSkus: Query;
		/** Admin only. */
		topStores?: Query;
	};
	/** Display names by id; an id with none reads as itself. */
	names: { store?: Map<string, string>; province: Map<string, string>; city: Map<string, string> };
	onDrill: (provinceId: string | null) => void;
	/** A chart card's export action (EP13-S18), by the block it draws. */
	exportAction?: (block: "trend" | "channels" | "geography", title: string) => ReactNode;
}

/** A card's loading, its refusal and its failure, each alone (`13/01` §8). */
export function Loaded({
	query,
	t,
	children,
}: {
	query: UseQueryResult;
	t: Translate;
	children: ReactNode;
}) {
	if (query.error instanceof ApiError && query.error.status === 403) {
		return (
			<ForbiddenState
				t={t}
				{...(query.error.permission ? { permission: query.error.permission } : {})}
			/>
		);
	}
	if (query.error) {
		return <ErrorState error={query.error} t={t} onRetry={() => void query.refetch()} />;
	}
	if (query.isPending) {
		return <Skeleton className="h-72 w-full" />;
	}
	return <>{children}</>;
}

export function SalesPanels({
	t,
	locale,
	variant,
	range,
	compare,
	province,
	blocks,
	names,
	onDrill,
	exportAction,
}: SalesPanelsProps) {
	const { summary, trend, channels, geography, topSkus, topStores } = blocks;
	const admin = variant === "admin";
	const unassigned = t("analytics.sales.unassigned");
	const labelOf = (map: Map<string, string>) => (id: string) =>
		id === UNASSIGNED ? unassigned : (map.get(id) ?? id);
	const storeLabel = labelOf(names.store ?? new Map());
	const channelLabel = (id: string) =>
		(SALES_CHANNELS as readonly string[]).includes(id)
			? t(`analytics.sales.channelNames.${id}`)
			: id;
	const day = (value: string) => formatDay(value, locale);
	const count = (value: number) => formatCount(value, locale);

	return (
		<>
			<Loaded query={summary} t={t}>
				{tilesOf(summary.data).map((tile) => (
					<Tiles
						key={tile.currency ?? "none"}
						tile={tile}
						trend={trend.data}
						range={range}
						t={t}
						locale={locale}
					/>
				))}
			</Loaded>
			<Loaded query={trend} t={t}>
				<PerCurrency block={trend.data} empty={t("analytics.sales.empty")}>
					{(currency) => {
						const data: ChartData = {
							x: { key: "date", label: t("analytics.sales.day"), format: day },
							series: [
								{ key: "current", label: t("analytics.sales.thisPeriod"), color: 1 },
								{ key: "previous", label: t("analytics.sales.previousPeriod"), color: 8 },
							],
							rows: trendRows(range, trend.data, currency, "revenueMinor", true),
							format: moneyIn(currency, locale),
						};
						return (
							<ChartCard
								key={currency}
								title={t("analytics.sales.trend.title", { currency })}
								summary={t("analytics.sales.trend.summary", {
									from: day(range.from),
									to: day(range.to),
								})}
								data={data}
								t={t}
								actions={exportAction?.("trend", t("analytics.sales.trend.title", { currency }))}
							>
								<LineTrend {...data} />
							</ChartCard>
						);
					}}
				</PerCurrency>
			</Loaded>
			<div className="grid gap-4 lg:grid-cols-2">
				<Loaded query={channels} t={t}>
					<PerCurrency block={channels.data} empty={t("analytics.sales.empty")}>
						{(currency) => (
							<BarsCard
								key={currency}
								title={t("analytics.sales.channels.title", { currency })}
								block={channels.data}
								dim="channel"
								currency={currency}
								compare={compare}
								label={channelLabel}
								t={t}
								locale={locale}
								actions={exportAction?.(
									"channels",
									t("analytics.sales.channels.title", { currency }),
								)}
							/>
						)}
					</PerCurrency>
				</Loaded>
				<Loaded query={geography} t={t}>
					<div className="flex flex-col gap-2">
						{province !== undefined && (
							<Button
								variant="secondary"
								size="sm"
								className="self-start"
								onClick={() => onDrill(null)}
							>
								{t("analytics.sales.geography.allProvinces")}
							</Button>
						)}
						<PerCurrency block={geography.data} empty={t("analytics.sales.empty")}>
							{(currency) => (
								<BarsCard
									key={currency}
									title={
										province === undefined
											? t("analytics.sales.geography.provinces", { currency })
											: t("analytics.sales.geography.cities", {
													currency,
													province: labelOf(names.province)(province),
												})
									}
									block={geography.data}
									dim={province === undefined ? "provinceId" : "cityId"}
									currency={currency}
									compare={compare}
									label={labelOf(province === undefined ? names.province : names.city)}
									t={t}
									locale={locale}
									actions={exportAction?.(
										"geography",
										t("analytics.sales.geography.provinces", { currency }),
									)}
									{...(province === undefined ? { onSelect: (id: string) => onDrill(id) } : {})}
								/>
							)}
						</PerCurrency>
					</div>
				</Loaded>
			</div>
			<div className="grid gap-4 lg:grid-cols-2">
				<TopTable
					query={topSkus}
					t={t}
					screenKey="analytics.sales.top_skus"
					caption={t("analytics.sales.topSkus.title")}
					note={t(admin ? "analytics.sales.topSkus.note" : "analytics.sales.topSkus.storeNote")}
					columns={[
						{
							id: "sku",
							headerKey: "analytics.sales.columns.sku",
							mono: true,
							card: "wide",
							cell: (row) => labelOf(new Map())(row.skuId ?? UNASSIGNED),
						},
						...(admin
							? [
									{
										id: "store",
										headerKey: "analytics.sales.columns.store",
										cell: (row: SalesRow) => storeLabel(row.storeId ?? ""),
									},
								]
							: []),
						{
							id: "units",
							headerKey: "analytics.sales.columns.units",
							align: "end",
							cell: (row) => count(row.units),
						},
						{
							id: "revenue",
							headerKey: "analytics.sales.columns.revenue",
							align: "end",
							cell: (row) => moneyCell(row, row.revenueMinor, locale),
						},
						{
							id: "share",
							headerKey: "analytics.sales.columns.share",
							align: "end",
							cell: (row) => formatRatio(row.shareBps, locale),
						},
					]}
				/>
				{admin && topStores !== undefined && (
					<TopTable
						query={topStores}
						t={t}
						screenKey="analytics.sales.top_stores"
						caption={t("analytics.sales.topStores.title")}
						columns={[
							{
								id: "store",
								headerKey: "analytics.sales.columns.store",
								card: "wide",
								cell: (row) => storeLabel(row.storeId ?? ""),
							},
							{
								id: "orders",
								headerKey: "analytics.sales.columns.orders",
								align: "end",
								cell: (row) => count(row.orders),
							},
							{
								id: "revenue",
								headerKey: "analytics.sales.columns.revenue",
								align: "end",
								cell: (row) => moneyCell(row, row.revenueMinor, locale),
							},
							{
								id: "aov",
								headerKey: "analytics.sales.columns.aov",
								align: "end",
								cell: (row) => moneyCell(row, row.aovMinor, locale),
							},
							{
								id: "delta",
								headerKey: "analytics.sales.columns.delta",
								align: "end",
								cell: (row) => {
									const pct = storeDelta(topStores.data, row);
									return pct === null ? "—" : `${pct > 0 ? "+" : ""}${count(pct)}%`;
								},
							},
						]}
					/>
				)}
			</div>
		</>
	);
}

function moneyIn(currency: string, locale: Locale) {
	return (amountMinor: number) => formatMoney({ amountMinor, currency }, locale);
}

/** An amount in its row's currency; a dash with none (AOV over no orders). */
function moneyCell(row: SalesRow, amountMinor: number | null, locale: Locale): string {
	return amountMinor === null || row.currency === null
		? "—"
		: formatMoney({ amountMinor, currency: row.currency }, locale);
}

/** One currency's four tiles: revenue, orders, units and AOV. */
function Tiles({
	tile,
	trend,
	range,
	t,
	locale,
}: {
	tile: SalesTile;
	trend: SalesBreakdown | undefined;
	range: DateRange;
	t: Translate;
	locale: Locale;
}) {
	const money = (amountMinor: number | null) =>
		amountMinor === null || tile.currency === null
			? "—"
			: formatMoney({ amountMinor, currency: tile.currency }, locale);
	const vs = t("analytics.sales.vsPrevious");
	const delta = (now: number | null, before: number | null | undefined) => {
		const pct = deltaPct(now, before);
		return pct === null ? {} : { delta: { pct, label: vs } };
	};
	const suffix = tile.currency === null ? "" : ` (${tile.currency})`;
	return (
		<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
			<KpiTile
				t={t}
				status="ready"
				label={t("analytics.sales.tiles.revenue") + suffix}
				value={tile.currency === null ? "—" : money(tile.revenue)}
				sparkline={sparkOf(range, trend, tile.currency, "revenueMinor")}
				{...delta(tile.revenue, tile.previous?.revenue)}
			/>
			<KpiTile
				t={t}
				status="ready"
				label={t("analytics.sales.tiles.orders") + suffix}
				value={formatCount(tile.orders, locale)}
				sparkline={sparkOf(range, trend, tile.currency, "orders")}
				{...delta(tile.orders, tile.previous?.orders)}
			/>
			<KpiTile
				t={t}
				status="ready"
				label={t("analytics.sales.tiles.units") + suffix}
				value={formatCount(tile.units, locale)}
				sparkline={sparkOf(range, trend, tile.currency, "units")}
				{...delta(tile.units, tile.previous?.units)}
			/>
			<KpiTile
				t={t}
				status="ready"
				label={t("analytics.sales.tiles.aov") + suffix}
				value={money(tile.aov)}
				{...delta(tile.aov, tile.previous?.aov)}
			/>
		</div>
	);
}

/** One card per currency the block has rows in, or a line saying there are none. */
function PerCurrency({
	block,
	empty,
	children,
}: {
	block: SalesBreakdown | undefined;
	empty: string;
	children: (currency: string) => ReactNode;
}) {
	const currencies = currenciesIn(block);
	if (currencies.length === 0) {
		return <p className="card p-4 text-[12.5px] text-fg-muted">{empty}</p>;
	}
	return <>{currencies.map(children)}</>;
}

/** Revenue by one dimension in one currency, with the previous period when compared. */
function BarsCard({
	title,
	block,
	dim,
	currency,
	compare,
	label,
	onSelect,
	t,
	locale,
	actions,
}: {
	title: string;
	actions?: ReactNode;
	block: SalesBreakdown | undefined;
	dim: SalesDim;
	currency: string;
	compare: boolean;
	label: (id: string) => string;
	onSelect?: (id: string) => void;
	t: Translate;
	locale: Locale;
}) {
	const rows = barRows(block, dim, currency, compare);
	const data: ChartData = {
		x: { key: "key", label: title, format: label },
		series: [
			{ key: "current", label: t("analytics.sales.thisPeriod"), color: 1 },
			...(compare
				? [{ key: "previous", label: t("analytics.sales.previousPeriod"), color: 8 }]
				: []),
		],
		rows,
		format: moneyIn(currency, locale),
	};
	return (
		<ChartCard title={title} summary={title} data={data} t={t} actions={actions}>
			<BarSeries
				{...data}
				{...(onSelect === undefined ? {} : { onSelect: (row) => onSelect(String(row.key)) })}
			/>
			{onSelect !== undefined && (
				<nav
					aria-label={t("analytics.sales.geography.drill")}
					className="mt-2 flex flex-wrap gap-1.5"
				>
					{rows
						.filter((row) => row.key !== UNASSIGNED)
						.map((row) => (
							<Button
								key={String(row.key)}
								variant="ghost"
								size="sm"
								onClick={() => onSelect(String(row.key))}
							>
								{label(String(row.key))}
							</Button>
						))}
				</nav>
			)}
		</ChartCard>
	);
}

/** A top-N table over one block's rows. */
function TopTable({
	query,
	t,
	screenKey,
	caption,
	note,
	columns,
}: {
	query: Query;
	t: Translate;
	screenKey: string;
	caption: string;
	note?: string;
	columns: Parameters<typeof DataTable<SalesRow>>[0]["columns"];
}) {
	const rows = query.data?.rows ?? [];
	return (
		<div className="flex flex-col gap-2">
			<DataTable<SalesRow>
				screenKey={screenKey}
				caption={caption}
				t={t}
				columns={columns}
				rowKey={(row) => `${row.storeId ?? ""}|${row.skuId ?? ""}|${row.currency ?? ""}`}
				page={{ items: rows, nextCursor: null }}
				status={query.isError ? "error" : query.isPending ? "loading" : "success"}
				error={query.error}
				onRetry={() => void query.refetch()}
				pagination={{
					showingFrom: rows.length === 0 ? 0 : 1,
					showingTo: rows.length,
					hasNext: false,
					hasPrevious: false,
					onNext: () => undefined,
					onPrevious: () => undefined,
				}}
				collapse="cards"
				minWidth={520}
				empty={<EmptyState title={t("analytics.sales.empty")} />}
			/>
			{note !== undefined && <p className="text-[12.5px] text-fg-muted">{note}</p>}
		</div>
	);
}
